import { memo, useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Gift, Zap, Star } from "lucide-react";
import { cn } from "@/lib/utils";

type BadgeVariant = "new" | "discover" | "tip" | "popular";

interface BadgeDescubrimientoProps {
  variant?: BadgeVariant;
  featureId: string;
  children?: React.ReactNode;
  className?: string;
  showOnce?: boolean;
  pulse?: boolean;
}

const VARIANT_CONFIG = {
  new: {
    icon: Sparkles,
    text: "Nuevo",
    className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-800",
  },
  discover: {
    icon: Gift,
    text: "Descubre",
    className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 border-purple-200 dark:border-purple-800",
  },
  tip: {
    icon: Zap,
    text: "Consejo",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800",
  },
  popular: {
    icon: Star,
    text: "Popular",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800",
  },
};

/**
 * BadgeDescubrimiento - Badge para destacar características nuevas o importantes
 * 
 * Puede configurarse para mostrarse solo una vez por usuario (usando localStorage)
 * y opcionalmente con animación de pulso para atraer atención.
 * 
 * @example
 * // Badge "Nuevo" que aparece solo una vez
 * <BadgeDescubrimiento variant="new" featureId="booking-system" showOnce />
 * 
 * // Badge "Descubre" con pulso
 * <BadgeDescubrimiento variant="discover" featureId="tribes" pulse />
 */
export const BadgeDescubrimiento = memo(({
  variant = "new",
  featureId,
  children,
  className,
  showOnce = false,
  pulse = false,
}: BadgeDescubrimientoProps) => {
  const [isVisible, setIsVisible] = useState(!showOnce);

  useEffect(() => {
    if (showOnce) {
      const storageKey = `badge-seen-${featureId}`;
      const hasSeen = localStorage.getItem(storageKey);
      
      if (!hasSeen) {
        setIsVisible(true);
        // Mark as seen after 5 seconds of visibility
        const timer = setTimeout(() => {
          localStorage.setItem(storageKey, "true");
        }, 5000);
        return () => clearTimeout(timer);
      } else {
        setIsVisible(false);
      }
    }
  }, [featureId, showOnce]);

  if (!isVisible) return null;

  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs font-medium border",
        config.className,
        pulse && "animate-pulse",
        className
      )}
    >
      <Icon className="h-3 w-3 mr-1" />
      {children || config.text}
    </Badge>
  );
});

BadgeDescubrimiento.displayName = "BadgeDescubrimiento";
